import { LandingSection } from '@features/landing/shared/ui/components/LandingSection';
import styles from './ChessOpenSection.module.css';
import { LandingSectionTitle } from '@features/landing/shared/ui/components/LandingSectionTitle';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { useEffect, useRef } from 'react';
import { useGSAP } from '@features/landing/shared/providers/GsapProvider';

export function ChessOpenSection() {
  const device = useMediaQuery();

  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const screenshotRef = useRef<HTMLImageElement>(null);

  const { gsap, ScrollTrigger } = useGSAP();

  useEffect(() => {
    if (!gsap || !ScrollTrigger) return;

    const ctx = gsap.context(() => {
      if (device === 'mobile') {
        gsap.to(screenshotRef.current, {
          scrollTrigger: {
            scrub: true,
            invalidateOnRefresh: true,
            trigger: screenshotRef.current,
            start: 'top bottom',
            end: 'bottom top',
          },
          x: () =>
            -1 *
            (Number(screenshotRef.current?.getBoundingClientRect().width) -
              window.innerWidth +
              40),
        });
      } else {
        const animationOptions = {
          trigger: rootRef.current,
          scrub: true,
          invalidateOnRefresh: true,
        };

        const isAnimationEnabled = () => {
          if (!rootRef.current) return false;
          return (
            rootRef.current.getBoundingClientRect().height > window.innerHeight
          );
        };

        const start = () => {
          if (!rootRef.current) return 'top top';
          const headerHeight = getComputedStyle(
            rootRef.current,
          ).getPropertyValue('--layout-header-height');
          return `top top+=${parseFloat(headerHeight)}`;
        };

        const end = () => {
          if (!rootRef.current) return 'bottom top';
          const headerHeight = getComputedStyle(
            rootRef.current,
          ).getPropertyValue('--layout-header-height');
          return `bottom top+=${parseFloat(headerHeight)}`;
        };

        gsap.to(headerRef.current, {
          scrollTrigger: { ...animationOptions, start, end },
          opacity: () => (isAnimationEnabled() ? 0 : 1),
          scale: () => (isAnimationEnabled() ? 0.9 : 1),
          y: () => (isAnimationEnabled() ? -100 : 0),
          ease: 'power3.out',
        });

        gsap.to(descriptionRef.current, {
          scrollTrigger: { ...animationOptions, start, end },
          opacity: () => (isAnimationEnabled() ? 0 : 1),
          scale: () => (isAnimationEnabled() ? 0.9 : 1),
          y: () => (isAnimationEnabled() ? -120 : 0),
          ease: 'power3.out',
        });

        gsap.to(screenshotRef.current, {
          scrollTrigger: { ...animationOptions, start, end },
          y: () =>
            isAnimationEnabled()
              ? -1 *
                (Number(headerRef.current?.getBoundingClientRect().height) +
                  Number(
                    descriptionRef.current?.getBoundingClientRect().height,
                  ))
              : 0,
        });
      }
    });

    return () => ctx.kill(true);
  }, [
    gsap,
    ScrollTrigger,
    device,
    rootRef,
    headerRef,
    descriptionRef,
    screenshotRef,
  ]);

  return (
    <div>
      <div ref={rootRef}>
        <LandingSection className={styles.root}>
          <div className={styles.content}>
            <div className={styles.header} ref={headerRef}>
              <img
                className={styles.logo}
                src="/images/landing/chessopen-logo.svg"
                width="231"
                height="96"
              />

              <LandingSectionTitle className={styles.title} preset="dark">
                Платформа ChessOpen&nbsp;—
                <br />
                место проведения крупнейших{' '}
                <span className={styles.titlePartNonMobile}>
                  российских турниров
                </span>
                <span className={styles.titlePartMobile}>турниров России</span>
              </LandingSectionTitle>
            </div>

            <div className={styles.description} ref={descriptionRef}>
              <div className={styles.descriptionItem}>
                Наша команда уже провела крупнейшие турниры России: Кубок России
                среди студентов, Первенство России среди студентов, Королевский
                гамбит
              </div>
              <div className={styles.descriptionItem}>
                Все партии транслируются на платформе в реальном времени, вы
                можете наблюдать за каждым ходом прямо в браузере, с оценкой
                ходов от Стокфиш
              </div>
              <div className={styles.descriptionItem}>
                Судьи с огромным опытом проведения онлайн- и оффлайн-турниров,
                при наличии подозрений, разбирают каждый случай отдельно
              </div>
            </div>

            <img
              ref={screenshotRef}
              className={styles.screenshot}
              src="/images/landing/chessopen-screenshot.png"
            />
          </div>
        </LandingSection>
      </div>
    </div>
  );
}
